import { Link } from '@tanstack/react-router';
import { KeyRound, Plug, Plus } from 'lucide-react';
import { ConnectionDialog } from '@/components/connection-dialog';
import { Page, PageHeader } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { WorkspaceSecretsSection } from '@/components/secrets';
import { ConnectionStatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useConnections, useWorkspaceId } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { CONNECTION_TYPE } from '@/lib/labels';

export function ConnectionsPage() {
  const workspaceId = useWorkspaceId();
  const connections = useConnections(workspaceId);

  return (
    <Page>
      <PageHeader
        title={t('Connections')}
        description={t('Connections to external systems. For each desk, you decide which connections its agents may use.')}
        actions={
          <ConnectionDialog workspaceId={workspaceId}>
            <Button>
              <Plus /> {t('Create connection')}
            </Button>
          </ConnectionDialog>
        }
      />
      {connections.isPending ? (
        <LoadingRows rows={3} />
      ) : connections.isError ? (
        <ErrorState error={connections.error} onRetry={() => void connections.refetch()} />
      ) : connections.data.length === 0 ? (
        <EmptyState
          icon={<Plug />}
          title={t('No connections yet')}
          description={t('Connect an external system, e.g. your ticket system or an internal API, so agents can get information from it.')}
          action={
            <ConnectionDialog workspaceId={workspaceId}>
              <Button>
                <Plus /> {t('Create your first connection')}
              </Button>
            </ConnectionDialog>
          }
        />
      ) : (
        <Card className="divide-y overflow-hidden">
          {connections.data.map((c) => (
            <Link
              key={c.id}
              to="/w/$workspaceId/connections/$connectionId"
              params={{ workspaceId, connectionId: c.id }}
              className="group flex items-center gap-4 px-4 py-3 hover:bg-muted/50"
            >
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <Plug className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-medium group-hover:text-primary">{c.name}</span>
                  {c.workspaceId !== workspaceId ? (
                    <Badge tone="outline" title={t('Shared by another workspace – it is changed there')}>
                      {t('from {workspace}', { workspace: c.workspaceName ?? '…' })}
                    </Badge>
                  ) : c.sharedWith === 'all' ? (
                    <Badge tone="outline">{t('Shared with all workspaces')}</Badge>
                  ) : (
                    c.sharedWith.length > 0 && <Badge tone="outline">{t('Shared with {n} workspaces', { n: c.sharedWith.length })}</Badge>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {c.managed === 'workspace_db' ? t('Workspace database · managed by Deskworks · every desk can use it') : (CONNECTION_TYPE[c.type]?.label ?? c.type)}
                  {!c.managed && c.description && ` · ${c.description}`}
                </div>
              </div>
              <div className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex" title={t('Credentials')}>
                <KeyRound className="size-3.5" />
                {c.hasCredential ? t('saved') : t('not saved')}
              </div>
              <div className="hidden w-28 text-right text-xs text-muted-foreground md:block">
                {c.lastHealthCheckAt ? (
                  tr('checked {time}', { time: <RelativeTime value={c.lastHealthCheckAt} /> })
                ) : (
                  t('never checked')
                )}
              </div>
              <ConnectionStatusBadge status={c.status} />
            </Link>
          ))}
        </Card>
      )}
      <WorkspaceSecretsSection workspaceId={workspaceId} />
    </Page>
  );
}
